import { AdjustmentsVerticalIcon } from '@heroicons/react/20/solid';
import { useFetcher } from '@remix-run/react';
import { clsx } from 'clsx';
import { Alert, Button } from 'flowbite-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useAudioGenerationController } from '#speaker-shop/hooks/useAudioGenerationController.ts';
import { useCookieBannerShow } from '#speaker-shop/hooks/useCookieBannerShow.ts';
import { type CoreTheme } from '#speaker-shop/hooks/useCoreTheme.ts';
import { useElevenLabsControls } from '#speaker-shop/hooks/useElevenLabsControls.ts';
import { useFingerprint } from '#speaker-shop/hooks/useFingerprint.ts';
import { useProgressTracking } from '#speaker-shop/hooks/useProgressTracking.ts';
import { useVoiceSlug } from '#speaker-shop/hooks/useVoiceSlug';
import { type VoiceData } from '#speaker-shop/models/speaker-voice-data.server.ts';
import { type AudioGenerationCreateResponse, type AudioGenerationLimitReachedResponse } from '#speaker-shop/utils/audio-generation-api.client.ts';
import { trackClickGenerateVoice, trackClickVoiceSettings, trackGenerationJobId } from '#speaker-shop/utils/tracking.ts';
import { useAudio } from './AudioContext';
import { ConsentOverlay } from './ConsentOverlay';
import { ContactFormDialog } from './ContactFormDialog';
import { Dialog } from './Dialog.tsx';
import { LimitReachedOverlay } from './LimitReachedOverlay';
import { ProgressBar } from './ProgressBar';
import { useRecordingContext } from './RecordingContext';
import { VoiceSettingsDialog } from './VoiceSettingsDialog';

interface TextAreaProps {
    rows: number;
    className?: string | ((hasError: boolean) => string);
}

export interface RenderablePart {
    render: () => React.ReactNode;
    has: boolean;
}

interface Opts {
    hasConsent: boolean;
    isLoggedIn: boolean;
    anonymousId: string | null;
    limits: {
        generations?: {
            max: number;
            used: number;
            remaining: number;
        };
        maxCharacters?: number;
    } | null;
    defaultAudioControls: VoiceData;
    generatedTakeName?: string;
    generatedSpeakerVoiceId?: string;
    progressBarVariant?: 'default' | 'unified';
    appearance?: 'legacy' | 'unified';
    theme?: CoreTheme;
    requestDataRefresh: () => void;
    children: (parts: {
        renderTextArea: (props: TextAreaProps) => React.ReactNode;
        renderForm: (children: React.ReactNode) => React.ReactNode;
        characterCounter: RenderablePart;
        characterLimitReached: RenderablePart;
        remainingGenerations: RenderablePart;
        consentOverlay: RenderablePart;
        limitOverlay: RenderablePart;
        voiceSettings: RenderablePart;
        progressBar: RenderablePart;
        autoplayBlocked: RenderablePart;
        error: string | null;
        isGenerating: boolean;
        isCancelling: boolean;
        canGenerate: boolean;
        isPlayingAudio: boolean;
        blockedAndUnchanged: boolean;
        onGenerate: () => Promise<void>;
        onCancel: () => Promise<void>;
        onStop: () => void;
    }) => React.ReactNode;
}

interface UsageResponse {
    isLoggedIn: boolean;
    hasConsent: boolean;
    generations: {
        max: number;
        used: number;
        remaining: number;
    } | null;
    maxCharacters: number | null;
}

function isLimitReachedResponse(
    result: AudioGenerationCreateResponse | null,
): result is AudioGenerationLimitReachedResponse {
    return !!result && 'limitReached' in result && result.limitReached === true && 'reason' in result;
}

export function AudioGeneration({
    hasConsent,
    isLoggedIn,
    anonymousId,
    limits,
    defaultAudioControls,
    generatedTakeName,
    generatedSpeakerVoiceId,
    progressBarVariant = 'default',
    appearance = 'legacy',
    theme = 'dark',
    requestDataRefresh,
    children,
}: Opts) {
    const { t, rawT } = useEditableTranslation('audio-generation');
    const { addTake } = useRecordingContext();
    const audioControls = defaultAudioControls;
    const voiceSlug = useVoiceSlug();
    const usageFetcher = useFetcher<UsageResponse>();
    const { stopAudio, playAudio, isPlayingAudio, hasPendingAudio, retryPendingAudio } = useAudio();
    const [ showContactForm, setShowContactForm ] = useState(false);
    const [ showCharacterLimitContactForm, setShowCharacterLimitContactForm ] = useState(false);
    const [ queuePosition, setQueuePosition ] = useState(0);
    const [ isVoiceSettingsOpen, setIsVoiceSettingsOpen ] = useState(false);
    const generatedTakeNameRef = useRef(generatedTakeName);
    const generatedSpeakerVoiceIdRef = useRef(generatedSpeakerVoiceId);
    const [ showLimitModal, setShowLimitModal ] = useState(false);
    const lastUsageAutoLoadKeyRef = useRef<string | null>(null);

    const refreshUsage = useCallback(() => {
        usageFetcher.load('/api/audio-generation/usage');
    }, [ usageFetcher ]);

    useEffect(() => {
        const autoLoadKey = JSON.stringify({ hasConsent, isLoggedIn, anonymousId });

        if (lastUsageAutoLoadKeyRef.current === autoLoadKey) {
            return;
        }

        lastUsageAutoLoadKeyRef.current = autoLoadKey;
        refreshUsage();
    }, [ anonymousId, hasConsent, isLoggedIn, refreshUsage ]);

    const effectiveLimits = useMemo(() => {
        if (!usageFetcher.data) {
            return limits;
        }

        return {
            generations: usageFetcher.data.generations ?? undefined,
            maxCharacters: usageFetcher.data.maxCharacters ?? undefined
        };
    }, [ usageFetcher.data, limits ]);

    const effectiveHasConsent = usageFetcher.data?.hasConsent ?? hasConsent;
    const effectiveIsLoggedIn = usageFetcher.data?.isLoggedIn ?? isLoggedIn;

    const canUseApp = effectiveHasConsent || effectiveIsLoggedIn;
    const hasReachedLimit = effectiveLimits && effectiveLimits.generations
        && effectiveLimits.generations.remaining === 0;
    const needsConsent = !canUseApp && !hasReachedLimit;

    useFingerprint(effectiveHasConsent, effectiveIsLoggedIn, anonymousId);

    const { voiceSettings, setVoiceSettings } = useElevenLabsControls(
        defaultAudioControls,
        generatedSpeakerVoiceId ?? voiceSlug
    );

    const {
        progressState,
        updateStage,
        clearProgress,
        startProgress,
        stepTimeoutWarning,
        progressError,
        setProgressError,
    } = useProgressTracking();

    const {
        text,
        characterCount,
        error,
        isGenerating,
        isCancelling,
        blockedAndUnchanged,
        isFlagged,
        flaggedJobId,
        flaggedCategories,
        updateText,
        submitGeneration,
        cancelGeneration,
        setIsFlagged,
    } = useAudioGenerationController({
        voiceSlug: generatedSpeakerVoiceId ? undefined : voiceSlug,
        speakerVoiceId: generatedSpeakerVoiceId,
        onGenerationCreated: (createdJobId, steps) => {
            startProgress(steps);
            trackGenerationJobId(createdJobId);
        },
        onStageChange: stage => {
            updateStage(stage);
        },
        onQueuePositionChange: setQueuePosition,
        onGenerationDone: (data, submittedText) => {
            addTake({
                takeId: data.takeId,
                name: generatedTakeNameRef.current,
                speakerVoiceId: generatedSpeakerVoiceIdRef.current,
                elevenLabsSpeakerVoiceIdId: data.elevenLabsSpeakerVoiceIdId,
                voiceType: 'elevenlabs',
                text: submittedText,
                audio: data.url,
                textId: data.textId,
                isChecked: true,
                filePath: data.filePath
            });
            if (data.url) {
                playAudio(data.url);
            }
            requestDataRefresh();
            refreshUsage();
        },
        onGenerationSettled: () => {
            clearProgress();
        }
    });

    const showLimitOverlay = !!hasReachedLimit && !isGenerating && !needsConsent && !isPlayingAudio;
    const showConsentOverlay = needsConsent;

    const { isCookieBannerVisible } = useCookieBannerShow();
    const prevBannerVisibleRef = useRef<boolean | undefined>(undefined);

    // When the cookie banner closes (user likely accepted), refresh security context
    useEffect(() => {
        const prev = prevBannerVisibleRef.current;
        prevBannerVisibleRef.current = isCookieBannerVisible;
        // if overlay is visible and banner just transitioned from visible -> hidden, refresh
        if (showConsentOverlay && prev === true && isCookieBannerVisible === false) {
            requestDataRefresh();
        }
    }, [ isCookieBannerVisible, showConsentOverlay, requestDataRefresh ]);

    const onStop = useCallback(() => {
        if (isPlayingAudio) {
            stopAudio();
        }
    }, [ isPlayingAudio, stopAudio ]);

    const onGenerate = useCallback(async () => {
        if (isPlayingAudio) {
            return;
        }

        setProgressError(null);
        trackClickGenerateVoice(characterCount);
        generatedTakeNameRef.current = generatedTakeName;
        generatedSpeakerVoiceIdRef.current = generatedSpeakerVoiceId;
        const result = await submitGeneration(voiceSettings);

        if (isLimitReachedResponse(result)) {
            requestDataRefresh();
            refreshUsage();

            if (result.reason === 'daily_limit') {
                setShowLimitModal(true);
            }
        }
    }, [
        characterCount,
        generatedTakeName,
        generatedSpeakerVoiceId,
        isPlayingAudio,
        refreshUsage,
        requestDataRefresh,
        setProgressError,
        submitGeneration,
        voiceSettings,
    ]);

    const characterLimitExceeded = !!effectiveLimits && !!effectiveLimits.maxCharacters
        && characterCount > effectiveLimits.maxCharacters;
    const maxGenerationsReached = !!effectiveLimits && !!effectiveLimits.generations
        && effectiveLimits.generations.remaining === 0;

    const disableGenerate = isGenerating || !text.length || blockedAndUnchanged
        || (!effectiveHasConsent && !effectiveIsLoggedIn)
        || characterLimitExceeded || maxGenerationsReached;

    const textDisabled = showConsentOverlay || showLimitOverlay;

    const textArea = ({ rows, className }: TextAreaProps) => {
        return (
            <textarea
                className={clsx(
                    className instanceof Function ? className(characterLimitExceeded) : className,
                    characterLimitExceeded
                        ? 'border-red-500 text-red-600 focus:border-red-500 focus:outline-red-500  focus:ring-red-500'
                        : 'border-primary focus:border-primary focus:outline-primary focus:ring-primary',
                    { 'pointer-events-none': showConsentOverlay || showLimitOverlay }
                )}
                rows={rows}
                placeholder={rawT('text-placeholder')}
                value={text}
                onChange={e => {
                    updateText(e.target.value);
                    setProgressError(null);
                }}
                disabled={!!textDisabled}
                name='text'
            />
        );
    };

    const onAnonymousVerificationComplete = useCallback(() => {
        requestDataRefresh();
        refreshUsage();
        setShowLimitModal(false);
    }, [ refreshUsage, requestDataRefresh ]);

    return (
        <>
            <div className='space-y-4'>
                {voiceSettings && (
                    <VoiceSettingsDialog
                        settings={voiceSettings}
                        defaultSettings={audioControls}
                        onChange={setVoiceSettings}
                        t={t}
                        open={isVoiceSettingsOpen}
                        onClose={() => setIsVoiceSettingsOpen(false)}
                    />
                )}
                {children({
                    renderTextArea: textArea,
                    renderForm: (children) => (
                        <form
                            onSubmit={async event => {
                                event.preventDefault();
                                await onGenerate();
                            }}
                        >
                            {children}
                        </form>
                    ),
                    characterCounter: {
                        has: !!effectiveLimits,
                        render: () => {
                            if (!effectiveLimits) {
                                return null;
                            }
                            return (
                                <div className='text-white'>
                                    {characterCount} / {effectiveLimits.maxCharacters} {t('characters')}
                                </div>
                            );
                        }
                    },
                    characterLimitReached: {
                        has: characterLimitExceeded,
                        render: () => {
                            if (!characterLimitExceeded || !effectiveLimits?.maxCharacters) {
                                return null;
                            }
                            return (
                                <Alert color='failure' className='mb-3 px-3 py-2.5'>
                                    <Trans
                                        t={rawT}
                                        i18nKey={!effectiveIsLoggedIn
                                            ? 'character-limit-exceeded-explanation-anonymous'
                                            : 'character-limit-exceeded-explanation'}
                                        tOptions={{ limit: effectiveLimits.maxCharacters }}
                                    >
                                        <button
                                            type='button'
                                            className='ml-1 font-semibold underline underline-offset-2'
                                            onClick={() => {
                                                if (effectiveIsLoggedIn) {
                                                    setShowCharacterLimitContactForm(true);
                                                } else {
                                                    setShowLimitModal(true);
                                                }
                                            }}
                                        >
                                        </button>
                                    </Trans>
                                </Alert>
                            );
                        }
                    },
                    remainingGenerations: {
                        has: !effectiveIsLoggedIn && !!effectiveLimits && !!effectiveLimits.generations,
                        render: () => {
                            if (effectiveIsLoggedIn || !effectiveLimits || !effectiveLimits.generations) {
                                return null;
                            }
                            return (
                                <div className='text-white'>
                                    {t('generations-remaining', { count: effectiveLimits.generations.remaining })}
                                </div>
                            );
                        }
                    },
                    consentOverlay: {
                        has: showConsentOverlay,
                        render: () => {
                            if (!showConsentOverlay) {
                                return null;
                            }
                            return <ConsentOverlay t={t} appearance={appearance} theme={theme} />;
                        }
                    },
                    limitOverlay: {
                        has: showLimitOverlay,
                        render: () => {
                            if (!showLimitOverlay) {
                                return null;
                            }
                            return (
                                <LimitReachedOverlay
                                    t={t}
                                    rawT={rawT}
                                    onVerified={onAnonymousVerificationComplete}
                                    appearance={appearance}
                                    theme={theme}
                                />
                            );
                        }
                    },
                    voiceSettings: {
                        has: !!voiceSettings,
                        render: () => {
                            if (!voiceSettings) {
                                return null;
                            }
                            return (
                                <Button
                                    onClick={() => {
                                        trackClickVoiceSettings();
                                        return setIsVoiceSettingsOpen(true);
                                    }}
                                    color='dark'
                                    outline={false}
                                    size='lg'
                                    fullSized={true}
                                    type='button'
                                    disabled={showLimitOverlay || showConsentOverlay}
                                >
                                    <AdjustmentsVerticalIcon className='w-5 h-5 mr-2' /> {t('voice-settings')}
                                </Button>
                            );
                        }
                    },
                    progressBar: {
                        has: isGenerating && !!progressState && !error,
                        render: () => {
                            if (!isGenerating || !progressState || error) {
                                return null;
                            }

                            return (
                                <ProgressBar
                                    progressState={progressState}
                                    stepTimeoutWarning={stepTimeoutWarning}
                                    progressError={progressError}
                                    setProgressError={setProgressError}
                                    queuePosition={queuePosition}
                                    variant={progressBarVariant}
                                />
                            );
                        }
                    },
                    autoplayBlocked: {
                        has: hasPendingAudio,
                        render: () => {
                            if (!hasPendingAudio) {
                                return null;
                            }
                            return (
                                <Alert color='success' className='p-3'>
                                    <div className='flex items-center justify-between gap-4 w-full'>
                                        <span>{t('autoplay-blocked')}</span>
                                        <Button
                                            color='yellow'
                                            className='rounded-lg'
                                            type='button'
                                            onClick={retryPendingAudio}
                                        >
                                            {t('buttons.play')}
                                        </Button>
                                    </div>
                                </Alert>
                            );
                        }
                    },
                    isGenerating,
                    isCancelling,
                    error,
                    canGenerate: !disableGenerate,
                    isPlayingAudio,
                    blockedAndUnchanged,
                    onGenerate,
                    onCancel: cancelGeneration,
                    onStop
                })}
                <Dialog
                    open={isFlagged}
                    onClose={() => setIsFlagged(false)}
                    title={t('moderation.flagged-title', 'Content Flagged')}
                    description={t('moderation.flagged-text-description')}
                    icon='heroicons--exclamation-triangle-solid'
                    type='alert'
                    showCloseIcon
                >
                    <div className='space-y-4 text-red-200'>
                        <ul className='space-y-4 text-white'>
                            {flaggedCategories.map((category, index) => <li key={index}>{category}</li>)}
                        </ul>
                        <p>{t('moderation.please-rephrase')}</p>
                        <p>{t('moderation.contact-support')}</p>
                        <Button
                            color='crispybluedark'
                            size='lg'
                            onClick={() => setShowContactForm(true)}
                            type='button'
                            className='rounded mx-auto'
                        >
                            {t('buttons.contact-support', 'Contact')}
                        </Button>
                    </div>
                    <ContactFormDialog
                        topic={rawT('audio-generation:flagged-by-moderation.email-topic', { jobId: flaggedJobId })}
                        fixedBodyPart={rawT('audio-generation:flagged-by-moderation.fixed-body-part', {
                            text,
                            moderationResult: flaggedCategories.join('\n')
                        })}
                        description={t('audio-generation:flagged-contact-description')}
                        open={showContactForm}
                        onClose={() => {
                            setShowContactForm(false);
                            setIsFlagged(false);
                        }}
                    />
                </Dialog>
            </div>
            {showLimitModal && (
                <LimitReachedOverlay
                    t={t}
                    rawT={rawT}
                    isModal={true}
                    onClose={() => setShowLimitModal(false)}
                    onVerified={onAnonymousVerificationComplete}
                    appearance={appearance}
                    theme={theme}
                />
            )}
            <ContactFormDialog
                topic={rawT('character-limit-contact-topic')}
                description={t('character-limit-contact-description')}
                open={showCharacterLimitContactForm}
                onClose={() => setShowCharacterLimitContactForm(false)}
                appearance={appearance}
                theme={theme}
            />
        </>
    );
}
